import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api";
import { toast } from "sonner";
import { PanelHeading } from "./PortalShell";
import { CalendarClock, Download, FileText, Loader2, Plus, ShieldAlert, Trash2 } from "lucide-react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const inp = "w-full border border-raf-sky px-3 py-2.5 outline-none focus:border-raf-blue text-sm";
const CATEGORY_OPTIONS = ["Policy", "Procedure", "Risk assessment", "Guidance", "Other"];

const statusForReview = (reviewDate) => {
  if (!reviewDate) return { label: "No review date", tone: "bg-slate-100 text-slate-700" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${reviewDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return { label: "Invalid review date", tone: "bg-amber-100 text-amber-800" };
  if (due < today) return { label: "Overdue", tone: "bg-red-100 text-red-800" };
  if (due.getTime() === today.getTime()) return { label: "Due today", tone: "bg-amber-100 text-amber-800" };
  return { label: "Scheduled", tone: "bg-emerald-100 text-emerald-800" };
};

export const HealthSafetyPanel = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [busyUpload, setBusyUpload] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    category: "Policy",
    review_date: "",
    reminder_days_before: 14,
    notes: "",
  });
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/health-safety/documents");
      const rows = Array.isArray(data) ? data : [];
      setDocs(rows);
      setEdits(Object.fromEntries(rows.map((doc) => [doc.id, {
        title: doc.title || "",
        category: doc.category || "Policy",
        review_date: doc.review_date || "",
        reminder_days_before: doc.reminder_days_before ?? 14,
        notes: doc.notes || "",
      }])));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async () => {
    if (!form.title.trim() || !file) {
      toast.error("Add a title and choose a file.");
      return;
    }
    setBusyUpload(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title);
      fd.append("category", form.category);
      fd.append("review_date", form.review_date || "");
      fd.append("reminder_days_before", String(Number(form.reminder_days_before) || 0));
      fd.append("notes", form.notes || "");
      await api.post("/health-safety/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("H&S document uploaded.");
      setForm({ title: "", category: "Policy", review_date: "", reminder_days_before: 14, notes: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload H&S document.");
    } finally {
      setBusyUpload(false);
    }
  };

  const saveDoc = async (docId) => {
    const edit = edits[docId];
    setBusyId(`save-${docId}`);
    try {
      await api.patch(`/health-safety/documents/${docId}`, {
        ...edit,
        reminder_days_before: Number(edit.reminder_days_before) || 0,
      });
      toast.success("Review settings saved.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save review settings.");
    } finally {
      setBusyId("");
    }
  };

  const markReviewed = async (docId) => {
    setBusyId(`review-${docId}`);
    try {
      await api.post(`/health-safety/documents/${docId}/mark-reviewed`);
      toast.success("Review marked complete.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark review complete.");
    } finally {
      setBusyId("");
    }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm("Delete this H&S document?")) return;
    setBusyId(`delete-${docId}`);
    try {
      await api.delete(`/health-safety/documents/${docId}`);
      toast.success("H&S document deleted.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete H&S document.");
    } finally {
      setBusyId("");
    }
  };

  const orderedDocs = useMemo(() => [...docs].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")), [docs]);

  return (
    <div>
      <PanelHeading
        title="Health & Safety"
        intro="Upload policies and safety documents, set review dates, and let the system remind the H&S lead when review deadlines approach."
      />

      <div className="bg-white border border-white p-5 mb-6">
        <h3 className="font-display font-bold text-raf-navy mb-3 flex items-center gap-2"><Plus size={16} /> Add H&S document</h3>
        <div className="grid lg:grid-cols-2 gap-3">
          <input className={inp} placeholder="Document title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          <select className={inp} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
            {CATEGORY_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <input type="date" className={inp} value={form.review_date} onChange={(e) => setForm((f) => ({ ...f, review_date: e.target.value }))} />
          <input type="number" min={0} max={365} className={inp} value={form.reminder_days_before} onChange={(e) => setForm((f) => ({ ...f, reminder_days_before: e.target.value }))} placeholder="Days before review" />
        </div>
        <textarea className={`${inp} mt-3`} rows={3} placeholder="Notes or review scope" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2.5 border border-dashed border-raf-sky text-raf-slate hover:border-raf-blue transition-colors">
            <FileText size={16} /> {file ? file.name : "Choose document"}
          </button>
          <button onClick={upload} disabled={busyUpload} className="inline-flex items-center gap-2 px-5 py-2.5 bg-raf-red text-white hover:bg-[#A00926] transition-colors disabled:opacity-60">
            {busyUpload ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Upload document
          </button>
        </div>
      </div>

      <div className="bg-white border border-white p-5">
        <h3 className="font-display font-bold text-raf-navy mb-3 flex items-center gap-2"><ShieldAlert size={16} /> Review register</h3>
        {loading ? (
          <div className="text-sm text-raf-slate">Loading H&S documents...</div>
        ) : orderedDocs.length === 0 ? (
          <div className="text-sm text-raf-slate">No H&S documents uploaded yet.</div>
        ) : (
          <div className="space-y-4">
            {orderedDocs.map((doc) => {
              const edit = edits[doc.id] || {
                title: doc.title || "",
                category: doc.category || "Policy",
                review_date: doc.review_date || "",
                reminder_days_before: doc.reminder_days_before ?? 14,
                notes: doc.notes || "",
              };
              const status = statusForReview(edit.review_date);
              return (
                <div key={doc.id} className="border border-raf-sky p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="font-semibold text-raf-navy">{doc.title}</div>
                      <div className="text-xs text-raf-slate">{doc.filename} · uploaded by {doc.uploaded_by_name || "Staff"}</div>
                    </div>
                    <span className={`text-xs px-2.5 py-1 ${status.tone}`}>{status.label}</span>
                  </div>
                  <div className="grid lg:grid-cols-2 gap-3">
                    <input className={inp} value={edit.title} onChange={(e) => setEdits((prev) => ({ ...prev, [doc.id]: { ...edit, title: e.target.value } }))} />
                    <select className={inp} value={edit.category} onChange={(e) => setEdits((prev) => ({ ...prev, [doc.id]: { ...edit, category: e.target.value } }))}>
                      {CATEGORY_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    <input type="date" className={inp} value={edit.review_date} onChange={(e) => setEdits((prev) => ({ ...prev, [doc.id]: { ...edit, review_date: e.target.value } }))} />
                    <input type="number" min={0} max={365} className={inp} value={edit.reminder_days_before} onChange={(e) => setEdits((prev) => ({ ...prev, [doc.id]: { ...edit, reminder_days_before: e.target.value } }))} />
                  </div>
                  <textarea className={`${inp} mt-3`} rows={3} value={edit.notes} onChange={(e) => setEdits((prev) => ({ ...prev, [doc.id]: { ...edit, notes: e.target.value } }))} />
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-raf-slate">
                    <span className="inline-flex items-center gap-1.5"><CalendarClock size={13} /> Review date: {edit.review_date || "Not set"}</span>
                    <span>Reminder lead time: {Number(edit.reminder_days_before) || 0} day(s)</span>
                    <span>Last reviewed: {doc.last_reviewed_at ? new Date(doc.last_reviewed_at).toLocaleDateString("en-GB") : "Not logged"}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a href={`${BASE_URL}/api/health-safety/documents/${doc.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-raf-blue text-raf-blue hover:bg-raf-blue hover:text-white transition-colors">
                      <Download size={13} /> Open document
                    </a>
                    <button type="button" onClick={() => saveDoc(doc.id)} disabled={busyId === `save-${doc.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-raf-blue text-white hover:bg-raf-navy transition-colors disabled:opacity-60">
                      {busyId === `save-${doc.id}` ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={13} />} Save review settings
                    </button>
                    <button type="button" onClick={() => markReviewed(doc.id)} disabled={busyId === `review-${doc.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-60">
                      {busyId === `review-${doc.id}` ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />} Mark reviewed
                    </button>
                    <button type="button" onClick={() => removeDoc(doc.id)} disabled={busyId === `delete-${doc.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60">
                      {busyId === `delete-${doc.id}` ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
